import { Divider, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import {
  NCCard,
  NCCard2SectionLayout,
  NCCardAnalysisTypeToggle,
  NCCardComplianceFooter,
  NCCardHeader,
  useNCCardContext,
} from '@/components/NCCard/NCCard';
import { CarbonExplainer } from '@/components/NCExplainers/CarbonExplainer';
import { COMPLIANCE_LOGOS_CARBON } from '@/config/constants';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { CarbonGraphTile } from './GraphTile';
import { CarbonStatsTile } from './StatsTile';

export const BelowGroundCarbon = () => {
  const { t } = useTranslation();

  const [showCard, setShowCard] = useState(true);

  if (!showCard) {
    return null;
  }

  return (
    <PlotCardWrapper name={t('global.analysis.carbonStorageBg')}>
      <NCCard explainer={<CarbonExplainer />} data-testid='carbon-nca'>
        <BelowGroundCarbonPlotCard setShowCard={setShowCard} />
      </NCCard>
    </PlotCardWrapper>
  );
};

const BelowGroundCarbonPlotCard = ({ setShowCard }: { setShowCard: (show: boolean) => void }) => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';
  const { analysisType } = useNCCardContext();

  const factNames = {
    total: {
      graph: IndicatorName.soil_carbon_historic_total_graph,
      benchmark: IndicatorName.permanent_soil_carbon_total_benchmark,
      latestAnalysis: IndicatorName.permanent_soil_carbon_total,
      oneYearUplift: IndicatorName.permanent_soil_carbon_previous_1y_uplift_total,
      oneYearPctChange: IndicatorName.permanent_soil_carbon_previous_1y_uplift_total_pct_change,
    },
    perHa: {
      graph: IndicatorName.soil_carbon_historic_per_ha_graph,
      benchmark: IndicatorName.permanent_soil_carbon_per_ha_benchmark,
      latestAnalysis: IndicatorName.permanent_soil_carbon_per_ha,
      oneYearUplift: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha,
      oneYearPctChange: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha_pct_change,
    },
  };

  const factSet = analysisType === 'total' ? factNames.total : factNames.perHa;
  const facts = usePlotFacts(Object.values(factSet));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factSet.graph)?.value;

  const benchmarkFact = {
    high: getFactFromFactArray<number>(facts, factSet.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    }),
    moderate: getFactFromFactArray<number>(facts, factSet.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    }),
    low: getFactFromFactArray<number>(facts, factSet.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    }),
  };

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.historic_below_ground_carbon;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);

  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const plot = usePlot().data;
  const plotArea = plot.area;

  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal, plotArea);

  const latestAnalysisFact = getFactFromFactArray<number>(facts, factSet.latestAnalysis);

  const oneYearUpliftFact = getFactFromFactArray<number>(facts, factSet.oneYearUplift);

  const oneYearPctChangeFact = getFactFromFactArray<number>(facts, factSet.oneYearPctChange);
  const hasChartData = !isNullish(graphData);
  const hasStatsData =
    !isNullish(latestAnalysisFact?.value) ||
    !isNullish(oneYearUpliftFact?.value) ||
    !isNullish(oneYearPctChangeFact?.value);

  useEffect(() => {
    if (!hasChartData && !hasStatsData) {
      setShowCard(false);
    }
  }, [hasChartData, hasStatsData, setShowCard]);

  return (
    <>
      <NCCardHeader title={t('global.analysis.carbonStorageBg')}>
        {!isSmallScreen && <NCCardAnalysisTypeToggle data-testid='carbon-toggle' />}
      </NCCardHeader>
      <NCCard2SectionLayout>
        <Stack className='items-center pt-4 sm:items-stretch sm:pt-0'>
          {isSmallScreen && <NCCardAnalysisTypeToggle data-testid='carbon-toggle' />}
          <CarbonGraphTile
            graphData={graphData}
            benchmarkFact={benchmarkFact}
            activeCustomBenchmark={displayCustomBenchmark}
            selectedBenchmarkId={selectedBenchmarkId}
            onBenchmarkSelect={setSelectedBenchmarkId}
          />
        </Stack>
        <CarbonStatsTile
          latestAnalysisFact={latestAnalysisFact}
          oneYearUpliftFact={oneYearUpliftFact}
          oneYearPctChangeFact={oneYearPctChangeFact}
          benchmarkFact={benchmarkFact}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_CARBON} />
    </>
  );
};
